import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
	Confirmation, LoadingIndicator, ErrorPage, Alert, Application, Button, Form, FormGroupInputControl, Icon, connectedComponent, clearExceptions
} from 'gwx-voyager-components';
import {
	updateProfile, loadProfile, deleteAttribute, resendConfirmationEmail
} from './profile.actions';
import { parseAttributes, profileName } from './ProfileUtils';
import AddressInput from '../AddressInput/AddressInput.react';

/**
 * Profile component provides a form used
 * to update a user's Profile.
 *
 * @class Profile
 * @extends {Component}
 */
class Profile extends Component {
	constructor(props) {
		super(props);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleUpdate = this.handleUpdate.bind(this);
		this.validateAddress = this.validateAddress.bind(this);
		this.removeAddress = this.removeAddress.bind(this);
		this.onClickRemoveAddress = this.onClickRemoveAddress.bind(this);
		this.discardConfirmation = this.discardConfirmation.bind(this);
		this.resendEmailConfirmation = this.resendEmailConfirmation.bind(this);
		this.inputDisplayNames = {
			'com.cerner.gwx.voyager.profiles.first.name': 'First Name',
			'com.cerner.gwx.voyager.profiles.last.name': 'Last Name'
		};
		this.state = {
			buttonsDisabled: false,
			confirmation: null,
			profileAttributes: {},
			emailAttributeMap: {},
			editProfile: {
				'com.cerner.gwx.voyager.profiles.first.name': null,
				'com.cerner.gwx.voyager.profiles.last.name': null
			},
			isFormInvalid: false
		};
	}

	componentDidMount() {
		this.props.fetchProfile(this.props.profileId);
	}

	static getDerivedStateFromProps(props, state) {
		const {
			profile, profileId
		} = props;

		const {
			profileAttributes
		} = state;

		const attributes = Object.assign(profileAttributes, parseAttributes(profile, profileId), {});

		const profileMap = state.editProfile || {};
		const attributeKeys = ['com.cerner.gwx.voyager.profiles.first.name', 'com.cerner.gwx.voyager.profiles.last.name'];
		attributeKeys.map((key) => {
			const attrKey = profileMap[key];
			profileMap[key] = attrKey || (attributes && attributes[key]);
			return true;
		});

		const emailMap = state.emailAttributeMap || {};
		const verifiedProfileEmails = (attributes && attributes['com.cerner.gwx.voyager.profiles.email']) || [];
		const pendingProfileEmails = (attributes && attributes['com.cerner.gwx.voyager.profiles.email.pending']) || [];
		const profileEmails = verifiedProfileEmails.length > 0 ? verifiedProfileEmails.concat(pendingProfileEmails) : pendingProfileEmails;

		const profileEmailMap = profileEmails && profileEmails.length > 0 && profileEmails.reduce((obj, item) => Object.assign(obj, { [item.value]: item }), {});

		return { profileAttributes: attributes, editProfile: profileMap, emailAttributeMap: { ...emailMap, ...profileEmailMap } };
	}

	/**
	 * Handles changing the value of an input.
	 *
	 * @param {Event} e
	 * 		The input change event.
	 */
	handleInputChange(e) {
		const { target } = e;
		const { editProfile } = this.state;
		this.setState({
			editProfile: {
				...editProfile,
				[target.name]: {
					...editProfile[target.name], key: target.name, value: target.value
				}
			}
		});
	}

	/**
	 * Handles updating the Profile attributes
	 *
	 * @param {Event} e
	 * 		The form accept event.
	 */
	handleUpdate(e) {
		e.preventDefault();
		const attributeArr = [];
		Object.keys(this.state.editProfile).forEach((key) => attributeArr.push(this.state.editProfile[key]));

		if (this.validateForm(attributeArr)) {
			this.props.updateProfile(attributeArr);
		} else {
			this.setState({ isFormInvalid: true });
		}
	}

	onClickRemoveAddress(e, id, address) {
		const confirmationProps = {
			body: 'Are you sure you wish to remove this email address?',
			id: `delete-${id}-confirmation`,
			onClickCancel: this.discardConfirmation,
			onClickConfirm: () => this.removeAddress(id, address, this.discardConfirmation),
			target: e.currentTarget,
			placement: 'right',
			title: 'Confirm Deletion'
		};
		this.setState({ confirmation: <Confirmation {...confirmationProps} /> });
	}

	validateForm(attributeArr) {
		let isFormValid = true;
		this.props.exceptions.fields = {};
		const isFirstNameValid = this.validateName(attributeArr[0].value);
		const isLastNameValid = this.validateName(attributeArr[1].value);

		if (!isFirstNameValid && !isLastNameValid) {
			isFormValid = false;
			this.props.exceptions.fields = {
				'com.cerner.gwx.voyager.profiles.first.name': 'First Name is required.',
				'com.cerner.gwx.voyager.profiles.last.name': 'Last Name is required.'
			};
		} else if (!isFirstNameValid) {
			isFormValid = false;
			this.props.exceptions.fields = {
				'com.cerner.gwx.voyager.profiles.first.name': 'First Name is required.'
			};
		} else if (!isLastNameValid) {
			isFormValid = false;
			this.props.exceptions.fields = {
				'com.cerner.gwx.voyager.profiles.last.name': 'Last Name is required.'
			};
		}
		return isFormValid;
	}

	validateName(inputName) {
		if (inputName && inputName.trim().length > 0) {
			return true;
		}
		return false;
	}

	discardConfirmation() {
		this.setState({ confirmation: null });
	}

	resendEmailConfirmation(id) {
		this.props.resendEmailConfirmation(id);
	}

	validateAddress(address, successCallback) {
		this.props.updateProfile([{ key: 'com.cerner.gwx.voyager.profiles.email.pending', value: address }]).then(() => {
			if (successCallback) {
				successCallback();
				const emailMap = this.state.emailAttributeMap;
				this.setState({ emailAttributeMap: { ...emailMap, [address]: { key: 'com.cerner.gwx.voyager.profiles.email.pending', value: address } } });
			}
		});
	}

	removeAddress(id, address, successCallback) {
		const emailMap = this.state.emailAttributeMap;
		this.props.deleteAttribute(id).then((response) => {
			if (response && !response.error) {
				delete emailMap[address];
				this.setState({ emailAttributeMap: emailMap, profileAttributes: {} });
			}
		}).then(successCallback);
	}

	render() {
		if (this.props.isFetching && (!this.props.profile || !this.props.profile[this.props.profileId])) {
			return <LoadingIndicator />;
		}

		if (!this.props.isFetching && !this.props.profile) {
			return <ErrorPage heading='Profile Not Found' details={`Could not find Profile with id ${this.props.profileId}.`} />;
		}

		const formProps = {
			buttonsDisabled: this.state.buttonsDisabled,
			legend: 'Profile',
			onClickDiscard: this.props.discard,
			onSubmit: this.handleUpdate,
			primaryButtonLabel: 'Save',
			secondaryButtonLabel: ''
		};

		const inputArr = Object.keys(this.inputDisplayNames).map((input) => {
			const inError = !!(this.props.exceptions && this.props.exceptions.fields && this.props.exceptions.fields[input]);
			return (
				<FormGroupInputControl
					control={({
						id: input,
						name: input,
						onChange: this.handleInputChange,
						value: (this.state.editProfile && this.state.editProfile[input] && this.state.editProfile[input].value) || '',
						componentRef: (ref) => (this[input] = ref),
						inError
					})}
					helpText={inError ? this.props.exceptions.fields[input] : null}
					key={input}
					label={this.inputDisplayNames[input]}
				/>
			);
		});

		const profileEmails = Object.keys(this.state.emailAttributeMap);
		const emails = profileEmails && profileEmails.length > 0
			? profileEmails.map((e) => {
				const email = this.state.emailAttributeMap[e];
				const isPending = email.key === 'com.cerner.gwx.voyager.profiles.email.pending';
				if (!email.value) {
					return null;
				}
				return (
					<li className='list-group-item'>
						<Icon
							iconName={!isPending ? 'check' : 'clock-o'}
							iconStyle={classnames({ 'text-success': !isPending }, { 'text-info': isPending }, 'fa-lg mr-2')}
							tooltip={!isPending ? 'Verified Address' : 'Pending Address'}
						/>
						{' '}
						{email.value}
						{' '}
						{isPending ? (
							<Button
								className='btn-link'
								id={`resend-confirmation-${email.id}`}
								title={`Resend Email Confirmation for ${email.value}`}
								key={`resend-confirmation-${email.id}`}
								onClick={() => this.resendEmailConfirmation(email.id)}
							>
								<Icon iconName='paper-plane-o' iconStyle='fa-lg' />
							</Button>
						) : null}
						<Button
							className='btn-link'
							id={`delete-${email.id}`}
							title={`Delete ${email.value}`}
							key={email.id}
							onClick={(event) => this.onClickRemoveAddress(event, email.id, email.value)}
						>
							<Icon iconName='trash-o' iconStyle='fa-lg' />
						</Button>
						{isPending ? <span className='col-md-12 ml-3 row alert alert-info'>This address is pending verification.  An email was sent to this address with instructions to verify.</span> : null}
					</li>
				);
			})
			: <li className='list-group-item'>None</li>;

		return (
			<Application
				navigation={{
					brand: {
						brandName: 'Cerner Care Notifications', brandIconName: 'id-badge', brandClass: 'brand-name', brandUrl: '/'
					},
					navigationRight: [{ type: 'dropdown', display: profileName(this.state.editProfile, this.props.username), options: [{ href: this.props.helpLink, display: 'Help' }, { href: this.props.logoutLink, display: 'Logout' }] }]
				}}
				timeout={{ delay: this.props.timeout }}
			>
				<div className='m-4'>
					<Button
						id='profile-back'
						onClick={() => this.props.back()}
						className='btn btn-outline-primary'
					>
						<Icon
							iconName='arrow-left'
							tooltip='Back to Application'
						/>
					</Button>
					<Form {...formProps}>
						{inputArr}
					</Form>
					<div className='card card-body bg-light my-3'>
						<div className='form-group-control form-row mb-5'>
							<legend>Emails</legend>
							{!this.props.isFetching && (!profileEmails || profileEmails.length === 0) ? <Alert classNames='col-md-12' type='warning' visible>An email is required to use Cerner Care Notifications</Alert> : null}
							<ul className='list-group list-group-flush w-100'>
								{emails}

							</ul>
							<div className='mt-3'>
								<AddressInput
									id='address-input'
									addAddress={
										(address, successCallback) => this.validateAddress(address, successCallback)
									}
								/>
							</div>
						</div>
					</div>
				</div>
				{this.state.confirmation}
			</Application>
		);
	}
}

Profile.defaultProps = {
	username: (window.gwx && window.gwx.ui && window.gwx.ui.username) || 'User',
	helpLink: window.gwx && window.gwx.ui && window.gwx.ui.props && window.gwx.ui.props.urls && window.gwx.ui.props.urls.help,
	logoutLink: (window.gwx && window.gwx.ui && window.gwx.ui.props && window.gwx.ui.props.urls && window.gwx.ui.props.urls.logout) || '/logout',
	timeout: window.gwx && window.gwx.ui && window.gwx.ui.props && window.gwx.ui.props.timeout,
	profileId: window.gwx && window.gwx.ui && window.gwx.ui.profileId,
	profile: null,
	exceptions: {}
};

Profile.propTypes = {
	exceptions: PropTypes.shape({
		fields: PropTypes.string,
		timeStamp: PropTypes.number
	}),
	updateProfile: PropTypes.func.isRequired,
	fetchProfile: PropTypes.func.isRequired,
	back: PropTypes.func.isRequired,
	deleteAttribute: PropTypes.func.isRequired,
	resendEmailConfirmation: PropTypes.func.isRequired,
	profileId: PropTypes.string,
	isFetching: PropTypes.bool.isRequired,
	profile: PropTypes.shape({
		id: PropTypes.string,
		attributes: PropTypes.arrayOf(PropTypes.string)
	}),
	u: PropTypes.string.isRequired,
	discard: PropTypes.func.isRequired,
	community: PropTypes.shape({
		name: PropTypes.string,
		id: PropTypes.string
	}).isRequired,
	username: PropTypes.string,
	timeout: PropTypes.string,
	helpLink: PropTypes.string,
	logoutLink: PropTypes.string
};

/**
 * Maps the current state of the Redux store to a set of
 * properties to be passed to the component.
 *
 * @param {object} state
 */
const mapStateToProps = (state, props) => {
	const {
		match: { params: { profileId } }
	} = props;

	const {
		entities: { profile },
		exceptions
	} = state;

	return {
		profileId,
		isFetching: profile && profile.isFetching,
		profile,
		exceptions
	};
};

/**
 * Creates a set of functions that utilize the dispatcher to
 * dispatch actions. These are the Action Creators for the
 * Profile component.
 *
 * @param {function} dispatch
 * 		The redux dispatcher.
 * @param {object} props
 * 		The props being passed to the component.
 */
const mapDispatchToProps = (dispatch, props) => ({
	/**
	 * Discards the form.
	 */
	discard: (response) => {
		if (!response || !response.error) {
			dispatch(clearExceptions);
			props.history.push('/');
		}
	},

	back: () => props.history.push('/'),
	/**
	 * Makes request to update the given Profile.
	 *
	 * @param {object} attribute
	 * 		The Profile attribute to update.
	 */
	updateProfile: (attribute) => (dispatch(
		updateProfile(props.match.params.profileId, attribute)
	).then((response) => {
		const exception = response.error;
		if (undefined !== exception && exception.status >= 400) {
			dispatch({
				type: 'TRIGGER_ALERT',
				alert: {
					type: 'danger',
					dismissable: true,
					children: exception.message,
					html: false
				}
			});
		}
	})
	),

	deleteAttribute: (id) => dispatch(deleteAttribute(props.match.params.profileId, id)),
	/**
	 * Makes request to fetch an Profile by the given id.
	 *
	 * @param {object} id
	 * 		The profile id.
	 */
	fetchProfile: (id) => dispatch(loadProfile(id)),

	resendEmailConfirmation: (id) => dispatch(resendConfirmationEmail(props.match.params.profileId, id))
});

const ProfileContainer = connectedComponent(Profile, mapStateToProps, mapDispatchToProps);

export { ProfileContainer as default, Profile };
